/** Tenant-admin: team list, invite modal, activate/deactivate, resend. */
import { useCallback, useEffect, useState } from "react";
import type { FormEvent } from "react";
import { ApiError } from "../../api/http";
import * as api from "../../api/team";
import { ASSIGNABLE_ROLES, type TeamMember } from "../../api/types";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../components/ToastContext";
import { Badge, Button, Card, Input, Label, Modal, Select, Spinner } from "../../components/ui";
import { useT } from "../../i18n";

export function memberStatus(member: TeamMember): "active" | "inactive" | "invited" {
  if (!member.is_active) return "inactive";
  if (!member.is_verified) return "invited";
  return "active";
}

export function TeamPage() {
  const t = useT();
  const { user } = useAuth();
  const { push } = useToast();
  const [members, setMembers] = useState<TeamMember[] | null>(null);
  const [showInvite, setShowInvite] = useState(false);

  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState("teacher");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setMembers(await api.listTeam());
  }, []);

  useEffect(() => {
    reload().catch(() => setMembers([]));
  }, [reload]);

  const onInvite = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.inviteMember({ email, full_name: fullName, role });
      push("success", `${email} — ${t("team.status.invited")}`);
      setShowInvite(false);
      setEmail("");
      setFullName("");
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.error.generic"));
    } finally {
      setBusy(false);
    }
  };

  const onToggleActive = async (member: TeamMember) => {
    try {
      await api.updateMember(member.id, { is_active: !member.is_active });
      await reload();
    } catch (err) {
      push("error", err instanceof ApiError ? err.message : t("common.error.generic"));
    }
  };

  const onResend = async (member: TeamMember) => {
    try {
      const res = await api.resendInvite(member.id);
      push("success", res.message);
    } catch (err) {
      push("error", err instanceof ApiError ? err.message : t("common.error.generic"));
    }
  };

  const STATUS_TONE = { active: "success", inactive: "danger", invited: "warning" } as const;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-extrabold">{t("team.title")}</h1>
        <Button onClick={() => setShowInvite(true)}>{t("team.invite")}</Button>
      </div>

      {members === null ? (
        <div className="grid place-items-center py-12">
          <Spinner size={28} />
        </div>
      ) : (
        <Card>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line text-left text-xs uppercase text-muted">
                <th className="pb-2">Name</th>
                <th className="pb-2">{t("auth.email")}</th>
                <th className="pb-2">{t("team.invite.role")}</th>
                <th className="pb-2">Status</th>
                <th className="pb-2" />
              </tr>
            </thead>
            <tbody>
              {members.map((member) => {
                const status = memberStatus(member);
                const isSelf = member.id === user?.id;
                return (
                  <tr key={member.id} className="border-b border-line/60 last:border-0">
                    <td className="py-3 font-semibold text-ink">{member.full_name}</td>
                    <td className="py-3 text-muted">{member.email}</td>
                    <td className="py-3">
                      <Badge tone="brand">{member.role}</Badge>
                    </td>
                    <td className="py-3">
                      <Badge tone={STATUS_TONE[status]}>{t(`team.status.${status}`)}</Badge>
                    </td>
                    <td className="py-3 text-right">
                      {status === "invited" && (
                        <Button variant="ghost" onClick={() => onResend(member)}>
                          {t("team.resend")}
                        </Button>
                      )}
                      {!isSelf && (
                        <Button
                          variant={member.is_active ? "ghost" : "secondary"}
                          onClick={() => onToggleActive(member)}
                        >
                          {member.is_active ? t("team.deactivate") : t("team.activate")}
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      <Modal open={showInvite} onClose={() => setShowInvite(false)} title={t("team.invite.title")}>
        <form onSubmit={onInvite} className="space-y-4">
          <div>
            <Label htmlFor="inviteName">{t("team.invite.name")}</Label>
            <Input id="inviteName" required value={fullName} onChange={(e) => setFullName(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="inviteEmail">{t("auth.email")}</Label>
            <Input id="inviteEmail" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="inviteRole">{t("team.invite.role")}</Label>
            <Select id="inviteRole" value={role} onChange={(e) => setRole(e.target.value)}>
              {ASSIGNABLE_ROLES.map((roleOption) => (
                <option key={roleOption} value={roleOption}>
                  {roleOption}
                </option>
              ))}
            </Select>
          </div>
          {error && <p role="alert" className="text-sm text-danger">{error}</p>}
          <Button type="submit" loading={busy} className="w-full">
            {t("team.invite.submit")}
          </Button>
        </form>
      </Modal>
    </div>
  );
}
